import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Col, Collapse, Modal, Row, Space, Spin, Table, Typography } from "antd";
import { SaveOutlined, UndoOutlined } from "@ant-design/icons";
import CustomRadioGroup from "../shared/form/reactHookForm/CustomRadioGroup";
import CustomSelect from "../shared/form/reactHookForm/CustomSelect";
import CustomInputTextArea from "../shared/form/reactHookForm/CustomInputTextArea";
import {
  readReminderPreferences,
  listReminderPreferenceChangeReasons,
  listReminderPreferenceStatuses,
  updateReminderPreferences,
  clearReminderPreferencesOperationResult,
} from "../../store/reminder/reminderActions";
import openNotificationOperationResult from "../shared/openNotificationOperationResult";
import { dateTimeFormat } from "../shared/formatting";
import dayjs from "dayjs";
import reminderPreferencesSchema from "./reminderPreferencesSchema";
import reminderPreferencesChangeReasonSchema from "./reminderPreferencesChangeReasonSchema";

const { Text } = Typography;

const defaultValues = {
  EmailPreferenceStatusID: 1,
  SMSPreferenceStatusID: 1,
};

const changeReasonDefaultValues = {
  ChangeReasonID: null,
  Comment: "",
};

const leftLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ReminderPreferencesPanel = ({ renewalKey, policyNumber }) => {
  const dispatch = useDispatch();

  const preferences = useSelector((state) => state.reminder.preferences);
  const loading = useSelector((state) => state.reminder.preferencesLoading);
  const updating = useSelector((state) => state.reminder.preferencesUpdating);
  const operationResult = useSelector(
    (state) => state.reminder.preferencesOperationResult
  );
  const changeReasons = useSelector(
    (state) => state.reminder.preferenceChangeReasons
  );
  const preferenceStatuses = useSelector(
    (state) => state.reminder.preferenceStatuses
  );

  const {
    handleSubmit,
    reset,
    control,
    trigger,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(reminderPreferencesSchema),
  });

  const {
    handleSubmit: handleChangeReasonSubmit,
    reset: resetChangeReason,
    control: changeReasonControl,
    formState: { isValid: isChangeReasonValid },
  } = useForm({
    mode: "onChange",
    defaultValues: changeReasonDefaultValues,
    resolver: yupResolver(reminderPreferencesChangeReasonSchema),
  });

  const [isChangeReasonModalOpen, setIsChangeReasonModalOpen] = useState(false);
  const pendingValuesRef = useRef(null);

  useEffect(() => {
    if (renewalKey) {
      dispatch(readReminderPreferences(renewalKey));
    }
    dispatch(listReminderPreferenceChangeReasons());
    dispatch(listReminderPreferenceStatuses());
  }, [renewalKey, dispatch]);

  useEffect(() => {
    if (preferences) {
      reset({
        EmailPreferenceStatusID: preferences.EmailPreferenceStatusID ?? 1,
        SMSPreferenceStatusID: preferences.SMSPreferenceStatusID ?? 1,
      });
    }
  }, [preferences, reset]);

  useEffect(() => {
    openNotificationOperationResult(operationResult, () =>
      dispatch(clearReminderPreferencesOperationResult())
    );
  }, [operationResult]);

  const handleSave = (values) => {
    pendingValuesRef.current = values;
    resetChangeReason(changeReasonDefaultValues);
    setIsChangeReasonModalOpen(true);
  };

  const handleConfirmChangeReason = (changeReasonValues) => {
    dispatch(
      updateReminderPreferences({
        ...pendingValuesRef.current,
        ...changeReasonValues,
        RenewalKey: renewalKey,
      })
    );
    pendingValuesRef.current = null;
    setIsChangeReasonModalOpen(false);
  };

  const handleCancelChangeReason = () => {
    pendingValuesRef.current = null;
    setIsChangeReasonModalOpen(false);
  };

  const handleResetValues = () => {
    reset();
    trigger();
  };

  const historyColumns = [
    {
      title: "Date/Time",
      dataIndex: "DateModified",
      key: "DateModified",
      render: (value) => (value ? dayjs(value).format(dateTimeFormat) : ""),
    },
    {
      title: "Changed By",
      dataIndex: "UserModified",
      key: "UserModified",
    },
    {
      title: "Change Reason",
      dataIndex: "RenewalReminderPreferenceChangeReasonDescription",
      key: "RenewalReminderPreferenceChangeReasonDescription",
    },
    {
      title: "SMS Reminders",
      dataIndex: "SMSPreferenceDescription",
      key: "SMSPreferenceDescription",
    },
    {
      title: "Email Reminders",
      dataIndex: "EmailPreferenceDescription",
      key: "EmailPreferenceDescription",
    },
    {
      title: "Comment",
      dataIndex: "Comment",
      key: "Comment",
    },
  ];

  const settingsPanelContent = (
    <Row gutter={32}>
      <Col span={12}>
        <CustomRadioGroup
          control={control}
          layout={leftLayout}
          formLabel="SMS reminders"
          name="SMSPreferenceStatusID"
          required
          customField1={preferenceStatuses.length}
          options={preferenceStatuses.map((s) => ({
            label: s.RenewalReminderPreferenceDescription,
            value: s.RenewalReminderPreferenceStatusID,
          }))}
        />
        <CustomRadioGroup
          control={control}
          layout={leftLayout}
          formLabel="Email reminders"
          name="EmailPreferenceStatusID"
          required
          customField1={preferenceStatuses.length}
          options={preferenceStatuses.map((s) => ({
            label: s.RenewalReminderPreferenceDescription,
            value: s.RenewalReminderPreferenceStatusID,
          }))}
        />
      </Col>
    </Row>
  );

  const items = [
    {
      key: "1",
      label: `Policy #${policyNumber ?? ""}`,
      children: settingsPanelContent,
    },
    {
      key: "2",
      label: "Change History",
      children: (
        <Table
          size="small"
          rowKey="RenewalReminderPreferenceHistoryID"
          columns={historyColumns}
          dataSource={preferences?.History ?? []}
          pagination={false}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {!preferences && !loading ? (
        <Alert
          type="error"
          message="Could not load reminder preferences for this renewal."
          showIcon
        />
      ) : (
        <>
          <Row style={{ paddingBottom: 8 }}>
            <Col span={24}>
              <Space>
                <Button
                  size="small"
                  type="primary"
                  disabled={!isDirty || !isValid}
                  loading={updating}
                  icon={<SaveOutlined />}
                  onClick={handleSubmit(handleSave)}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  type="dashed"
                  disabled={!isDirty || updating}
                  icon={<UndoOutlined />}
                  onClick={handleResetValues}
                >
                  Reset Values
                </Button>
              </Space>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Collapse
                defaultActiveKey={["1", "2"]}
                size="small"
                items={items}
              />
            </Col>
          </Row>

          <Modal
            title="Reason for Change"
            open={isChangeReasonModalOpen}
            onOk={handleChangeReasonSubmit(handleConfirmChangeReason)}
            onCancel={handleCancelChangeReason}
            okButtonProps={{ disabled: !isChangeReasonValid, loading: updating }}
            okText="Save"
          >
            <CustomSelect
              control={changeReasonControl}
              layout={leftLayout}
              formLabel="Reason for change"
              name="ChangeReasonID"
              required
              size="small"
              options={changeReasons.map((r) => ({
                value: r.RenewalReminderPreferenceChangeReasonID,
                label: r.RenewalReminderPreferenceChangeReasonDescription,
              }))}
            />
            <CustomInputTextArea
              control={changeReasonControl}
              layout={leftLayout}
              formLabel="Comment"
              name="Comment"
              rows={2}
            />
          </Modal>
        </>
      )}
    </Spin>
  );
};

export default ReminderPreferencesPanel;
